import React, { useLayoutEffect, useState } from 'react';
import { createPortal } from 'react-dom';

export interface PortalProps {
  children: React.ReactNode;
  rootId?: string;
}

/**
 * This is a generic Portal component to make it easier to set up your component inside a portal.
 * Wrap your component with <Portal>{content}</Portal>, and your component will be rendered inside a
 * new DOM layer, independent of the base DOM. Short intro:
 * https://medium.com/@rahulrulz680/react-portals-and-use-cases-8f3bdc1fff68
 */
const Portal = ({ children, rootId = 'root' }: PortalProps) => {
  const [wrapperElement, setWrapperElement] = useState<HTMLElement | null>(null);

  // This effect will safely append your content, even if the DOM has not loaded your root element.
  useLayoutEffect(() => {
    let element = document.getElementById(rootId);
    // if element is not found with wrapperId or wrapperId is not provided,
    // create and append to body
    if (!element) {
      element = document.getElementById('root') || document.querySelector('[role="main"]');
    }
    setWrapperElement(element);
  }, [rootId]);

  // wrapperElement state will be null on the very first render.
  if (wrapperElement === null) return null;

  return createPortal(children, wrapperElement);
};

export default React.forwardRef(Portal);
